import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { FileDown, Check, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Layout, Brand } from "@/components/Layout";
import { usePatientMode } from "@/lib/patientMode";
import { downloadElementsAsPdf } from "@/lib/pdf";
import { BOITE_INTRO, BOITE_PRINCIPE, BOITE_SECTIONS, BOITE_FORMULATION, BOITE_CENTRAL } from "@/data/boiteOutilsTdah";
import { NIVEAUX, BOITE_NIVEAUX } from "@/data/boiteOutilsNiveaux";
import { Blocks } from "@/components/guideTdah/guideBlocks";
import { PdfRunner } from "@/components/guideTdah/GuidePdf";
import { EnvoyerBloc } from "@/components/EnvoyerBloc";

const FOOTER = "Boîte à outils TDAH — préconisations scolaires · M2HAT Psychologie";

const Formulation = ({ pdf }) => (
  <div className={`rounded-3xl border-l-4 border-[var(--gold)] bg-[var(--sand)] ${pdf ? "p-6" : "p-6 sm:p-8"}`} data-testid="boite-formulation">
    <h2 className="font-serif-title text-2xl text-[var(--gold)] mb-3">{BOITE_FORMULATION.title}</h2>
    <p className="text-[var(--ink)] leading-relaxed italic">{BOITE_FORMULATION.text}</p>
  </div>
);

const Central = ({ pdf }) => (
  <div className={`rounded-3xl bg-[var(--ink)] text-white ${pdf ? "p-6" : "p-6 sm:p-8"}`} data-testid="boite-central">
    <h2 className="font-serif-title text-2xl mb-4" style={{ color: "#D4A77B" }}>{BOITE_CENTRAL.title}</h2>
    <p className="text-sm opacity-80 mb-3">La question à poser n'est pas uniquement :</p>
    <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-4 items-center">
      <div className="rounded-2xl border border-white/20 p-4 flex gap-3"><X size={18} className="shrink-0 mt-0.5 opacity-70" /><span className="leading-relaxed">{BOITE_CENTRAL.bad}</span></div>
      <div className="hidden sm:flex flex-col items-center text-xs opacity-80"><ArrowRight size={22} style={{ color: "#D4A77B" }} />mais</div>
      <div className="rounded-2xl bg-white/10 border border-white/30 p-4 flex gap-3"><Check size={18} className="shrink-0 mt-0.5" style={{ color: "#D4A77B" }} /><span className="leading-relaxed font-semibold">{BOITE_CENTRAL.good}</span></div>
    </div>
  </div>
);

const levelsFor = (niveau) => (niveau === "tous" ? NIVEAUX : NIVEAUX.filter((n) => n.id === niveau));

const NiveauBoxes = ({ sectionId, niveau, pdf }) => {
  const data = BOITE_NIVEAUX[sectionId];
  if (!data) return null;
  const levels = levelsFor(niveau);
  return (
    <div className={`grid gap-3 ${levels.length > 1 && !pdf ? "md:grid-cols-3" : levels.length > 1 ? "grid-cols-3" : ""}`} data-testid={pdf ? undefined : `boite-niveaux-${sectionId}`}>
      {levels.map((n) => (
        <div key={n.id} className="g-tint rounded-2xl p-4 border-l-4" style={{ background: n.soft, borderColor: n.color, "--part": n.color }} data-testid={pdf ? undefined : `boite-niveau-box-${sectionId}-${n.id}`}>
          <div className="text-xs font-bold uppercase tracking-[0.18em] mb-2" style={{ color: n.color }}>{n.label} <span className="font-medium normal-case tracking-normal opacity-80">· {n.short}</span></div>
          <ul className="space-y-1.5">{(data[n.id] || []).map((it) => <li key={it} className="flex items-start gap-2 text-[var(--ink)] leading-snug text-[0.95em]"><span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: n.color }} />{it}</li>)}</ul>
        </div>
      ))}
    </div>
  );
};

const Section = ({ s, pdf, niveau = "tous" }) => (
  <section id={pdf ? undefined : `boite-${s.id}`} data-pdf-breakable={pdf ? "" : undefined} className={pdf ? "pt-6 pb-4 border-t border-[#E5DCD1]" : "rounded-3xl bg-white/80 border border-[var(--border)] p-6 sm:p-8 scroll-mt-6"} data-testid={pdf ? undefined : `boite-section-${s.id}`}>
    <div>
      <div className="flex items-start gap-3 mb-4">
        <span className="w-10 h-10 rounded-full bg-[var(--gold)] text-white font-bold flex items-center justify-center shrink-0">{s.n}</span>
        <div><h2 className="font-serif-title text-2xl sm:text-3xl text-[var(--ink)] leading-tight">{s.title}</h2>{s.sub && <p className="text-[var(--ink-soft)] mt-1">{s.sub}</p>}</div>
      </div>
      {pdf && <Blocks blocks={s.blocks.slice(0, 1)} color="var(--gold)" />}
    </div>
    {pdf ? <div className="mt-5"><Blocks blocks={s.blocks.slice(1)} color="var(--gold)" /></div> : <Blocks blocks={s.blocks} color="var(--gold)" />}
    <div className="mt-5"><NiveauBoxes sectionId={s.id} niveau={niveau} pdf={pdf} /></div>
  </section>
);

const pdfPages = (niveau) => [
  <div key="cover" className="flex flex-col">
    <div className="text-center pt-6"><Brand /></div>
    <div className="text-center mt-12">
      <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#B58C67] mb-4">Préconisations scolaires centrées sur les solutions</div>
      <h1 className="font-serif-title text-5xl text-[#4A3B36] mb-4">{BOITE_INTRO.title}</h1>
      <div className="flex justify-center gap-2 mb-6">{levelsFor(niveau).map((n) => <span key={n.id} className="px-4 py-1.5 rounded-full text-white text-sm font-bold" style={{ background: n.color }}>{n.label} · {n.short}</span>)}</div>
      <p className="text-[#6E5C53] leading-relaxed max-w-xl mx-auto">{BOITE_INTRO.lead}</p>
      <p className="text-sm text-[#B58C67] font-semibold mt-4">Document destiné aux parents, à partager avec l'équipe éducative de l'enfant.</p>
    </div>
    <div className="mt-10 rounded-3xl bg-[#F6EEE4] p-6"><h2 className="font-serif-title text-2xl text-[#B58C67] mb-3">{BOITE_PRINCIPE.title}</h2><Blocks blocks={BOITE_PRINCIPE.blocks} color="#B58C67" /></div>
  </div>,
  <div key="body" className="text-[13px] leading-relaxed">{BOITE_SECTIONS.map((s) => <Section key={s.id} s={s} pdf niveau={niveau} />)}</div>,
  <div key="end" className="space-y-6 text-[13px]"><Formulation pdf /><Central pdf /></div>,
];

const NiveauChips = ({ value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {[{ id: "tous", label: "Tous les niveaux", color: "#4A3B36" }, ...NIVEAUX].map((n) => (
      <button key={n.id} onClick={() => onChange(n.id)} data-testid={`${testid}-${n.id}`} aria-pressed={value === n.id}
        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${value === n.id ? "text-white shadow-sm" : "bg-white text-[var(--ink)] border-[var(--border)] hover:border-[var(--gold)]"}`}
        style={value === n.id ? { background: n.color, borderColor: n.color } : undefined}>{n.label}{n.short ? <span className="font-medium opacity-80"> · {n.short}</span> : ""}</button>
    ))}
  </div>
);

export default function BoiteOutilsModule() {
  const { patientMode } = usePatientMode();
  const [niveau, setNiveau] = useState("tous");
  const [ask, setAsk] = useState(false);
  const [pdfNiveau, setPdfNiveau] = useState("tous");
  const [job, setJob] = useState(null);
  const onReady = useCallback(async (els) => {
    if (!job) return;
    const suffix = job === "tous" ? "" : `-${job}`;
    try { await downloadElementsAsPdf(els, `boite-a-outils-tdah-m2hat${suffix}.pdf`, { footer: FOOTER }); toast.success("PDF généré"); }
    catch { toast.error("Export PDF impossible"); }
    finally { setJob(null); }
  }, [job]);
  const openAsk = () => { setPdfNiveau(niveau); setAsk(true); };
  const launch = () => { setAsk(false); setJob(pdfNiveau); };

  return (
    <Layout wide>
      <div className="animate-fade-up" data-testid="boite-outils-module">
        {!patientMode && <Link to="/" className="text-sm text-[var(--gold)] hover:underline no-print" data-testid="boite-back-home">← Retour à l'accueil</Link>}
        <header className="mt-4 mb-8 grid lg:grid-cols-[1.4fr_1fr] gap-6 items-end">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[var(--gold)]">{BOITE_INTRO.subtitle}</div>
            <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--ink)] leading-tight mt-1">{BOITE_INTRO.title}</h1>
            <p className="text-[var(--ink)] leading-relaxed mt-4 max-w-2xl" data-testid="boite-lead">{BOITE_INTRO.lead}</p>
            <p className="text-sm text-[var(--ink-soft)] mt-3" data-testid="boite-audience">Document destiné aux parents, à partager avec l'équipe éducative de l'enfant.</p>
          </div>
          <div className="flex lg:justify-end">
            <button onClick={openAsk} disabled={!!job} data-testid="boite-pdf" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--gold)] text-white font-semibold shadow-sm hover:brightness-105 active:scale-95 transition-all disabled:opacity-60"><FileDown size={18} /> {job ? "Préparation…" : "Télécharger la boîte à outils (PDF)"}</button>
          </div>
        </header>

        <div className="rounded-3xl bg-white/70 border border-[var(--border)] p-5 sm:p-6 mb-8 flex flex-col md:flex-row md:items-center gap-4" data-testid="boite-niveau-selector">
          <div className="md:w-72 shrink-0">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--gold)]">Déclinaison par niveau</div>
            <p className="text-sm text-[var(--ink-soft)] mt-1">Le tronc commun reste affiché ; l'encadré du niveau choisi apparaît dans chaque section.</p>
          </div>
          <NiveauChips value={niveau} onChange={setNiveau} testid="boite-niveau" />
        </div>

        {ask && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(74,59,54,0.45)", backdropFilter: "blur(2px)" }} data-testid="boite-pdf-dialog" onClick={() => setAsk(false)}>
            <div className="w-full max-w-lg rounded-3xl bg-white p-6 sm:p-8 shadow-xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-serif-title text-2xl text-[var(--ink)] mb-1">Quel niveau pour le PDF ?</h3>
              <p className="text-sm text-[var(--ink-soft)] mb-5">Tronc commun + encadrés du niveau choisi. « Tous les niveaux » inclut les trois.</p>
              <NiveauChips value={pdfNiveau} onChange={setPdfNiveau} testid="boite-pdf-niveau" />
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setAsk(false)} data-testid="boite-pdf-cancel" className="px-5 py-2.5 rounded-full border border-[var(--border)] text-sm font-semibold text-[var(--ink)]">Annuler</button>
                <button onClick={launch} data-testid="boite-pdf-confirm" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--gold)] text-white text-sm font-semibold shadow-sm"><FileDown size={16} /> Générer le PDF</button>
              </div>
            </div>
          </div>,
          document.body
        )}

        <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-8" data-testid="boite-principe">
          <h2 className="font-serif-title text-2xl sm:text-3xl text-[var(--gold)] mb-4">{BOITE_PRINCIPE.title}</h2>
          <Blocks blocks={BOITE_PRINCIPE.blocks} color="var(--gold)" />
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-64 shrink-0 no-print">
            <nav className="lg:sticky lg:top-6 rounded-3xl bg-white/70 border border-[var(--border)] p-4 flex flex-wrap lg:flex-col gap-1.5 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto" data-testid="boite-sommaire">
              {BOITE_SECTIONS.map((s) => (
                <a key={s.id} href={`#boite-${s.id}`} data-testid={`boite-toc-${s.id}`} className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-[var(--ink)] hover:bg-[var(--sand)] transition-colors">
                  <span className="w-6 h-6 rounded-full bg-[var(--sand)] text-[var(--gold)] text-[11px] font-bold flex items-center justify-center shrink-0">{s.n}</span>{s.title}
                </a>
              ))}
              <a href="#boite-formulation" data-testid="boite-toc-formulation" className="rounded-xl px-3 py-2 text-sm font-semibold text-[var(--gold)] hover:bg-[var(--sand)]">Formulation pour un compte rendu</a>
            </nav>
          </aside>
          <main className="flex-1 min-w-0 space-y-6">
            {BOITE_SECTIONS.map((s) => <Section key={s.id} s={s} niveau={niveau} />)}
            <div id="boite-formulation" className="scroll-mt-6"><Formulation /></div>
            <Central />
            <EnvoyerBloc
              testid="boite-envoyer"
              url={`${window.location.origin}/p/boite-outils-tdah`}
              title="Boîte à outils TDAH — préconisations scolaires"
              subject="Boîte à outils TDAH — préconisations scolaires pour la classe"
              message={"Bonjour,\nVoici une boîte à outils de préconisations scolaires centrées sur les solutions pour les élèves avec TDAH (à favoriser / à éviter, par niveau). Elle se consulte en ligne et se télécharge en PDF :"}
              intro="Partagez cette page avec l'enseignant(e) ou l'équipe éducative : lien direct, QR code à scanner, ou message prêt à envoyer par e-mail ou WhatsApp."
            />
            <div className="pt-4 text-center no-print">
              <button onClick={openAsk} disabled={!!job} data-testid="boite-pdf-bottom" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--ink)] text-white font-semibold shadow-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-60"><FileDown size={18} /> Télécharger la boîte à outils (PDF)</button>
            </div>
          </main>
        </div>
        {job && <PdfRunner pages={pdfPages(job)} onReady={onReady} />}
      </div>
    </Layout>
  );
}
